import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { studentAPI } from '../../services/api';
import './StudentDashboard.css';

function StudentDashboard({ user, onLogout }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getExercises();
      setExercises(response.data.exercises);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load exercises');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading exercises...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="student-dashboard">
        <h1>My Exercises</h1>

        {exercises.length === 0 ? (
          <div className="card">
            <p>No exercises available yet. Your teacher will assign exercises to you.</p>
          </div>
        ) : (
          <div className="exercises-grid">
            {exercises.map(exercise => (
              <div key={exercise.id} className={`exercise-card ${!exercise.hasAccess ? 'locked' : ''}`}>
                <div className="exercise-header">
                  <h2>{exercise.title}</h2>
                  <span className={`badge badge-${getStatusColor(exercise.status, exercise.hasAccess)}`}>
                    {getStatusLabel(exercise.status, exercise.hasAccess)}
                  </span>
                </div>

                <p className="exercise-description">{exercise.description}</p>

                {exercise.hasAccess ? (
                  <>
                    <div className="exercise-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${exercise.progress.percentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {exercise.progress.tasksCompleted} / {exercise.progress.totalTasks} tasks completed
                      </span>
                    </div>

                    {exercise.quizAttempts > 0 && exercise.status !== 'COMPLETED' && (
                      <div className="quiz-info">
                        Quiz attempts: {exercise.quizAttempts}
                      </div>
                    )}

                    <div className="exercise-actions">
                      {exercise.status === 'COMPLETED' ? (
                        <Link to={`/student/exercises/${exercise.id}`} className="btn btn-primary">
                          Review Exercise
                        </Link>
                      ) : exercise.status === 'QUIZ_PENDING' ? (
                        <Link to={`/student/exercises/${exercise.id}/quiz`} className="btn btn-success">
                          Take Final Quiz
                        </Link>
                      ) : (
                        <Link to={`/student/exercises/${exercise.id}`} className="btn btn-primary">
                          {exercise.status === 'NOT_STARTED' ? 'Start Exercise' : 'Continue'}
                        </Link>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="locked-message">
                    <div className="lock-icon">🔒</div>
                    <p>This exercise is locked. Your teacher needs to grant you access.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function getStatusColor(status, hasAccess) {
  if (!hasAccess) return 'locked';
  switch (status) {
    case 'NOT_STARTED': return 'info';
    case 'IN_PROGRESS': return 'warning';
    case 'QUIZ_PENDING': return 'warning';
    case 'COMPLETED': return 'success';
    default: return 'secondary';
  }
}

function getStatusLabel(status, hasAccess) {
  if (!hasAccess) return 'LOCKED';
  switch (status) {
    case 'NOT_STARTED': return 'NOT STARTED';
    case 'IN_PROGRESS': return 'IN PROGRESS';
    case 'QUIZ_PENDING': return 'QUIZ TO PASS';
    case 'COMPLETED': return 'COMPLETED';
    default: return status.replace('_', ' ');
  }
}

export default StudentDashboard;
